import { useCallback, useState } from 'react';

import { useMplServiceClient } from '../../contexts/MplServiceClientContext';

export interface AttachmentsGuidelineLinkViewModel {
  /** True while the guidelines URL is being fetched. Bind to the button's `loading` prop. */
  readonly isLoading: boolean;
  /**
   * Error copy to show when the fetch fails. Undefined when no error is
   * pending. The View should render a dismissable modal with this text.
   */
  readonly errorMessage: string | undefined;
  /**
   * Fetches a presigned guidelines-deck URL and opens it in a new tab.
   * Surfaces failures via {@link errorMessage} instead of throwing.
   */
  readonly openGuidelinesDeck: () => Promise<void>;
  /** Clears {@link errorMessage}. Used by the error modal's Dismiss button. */
  readonly dismissError: () => void;
}

const FETCH_FAILURE_MESSAGE =
  'We could not load the architecture-diagram guidelines. Please try again in a moment.';

/** Presentation state for the "How to build architecture diagrams" link. */
export function useAttachmentsGuidelineLinkViewModel(): AttachmentsGuidelineLinkViewModel {
  const mpl = useMplServiceClient();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  const dismissError = useCallback(() => setErrorMessage(undefined), []);

  // When the user clicks the link, fetch a fresh presigned URL and open
  // it. On failure, surface a dismissable error so the user can retry —
  // an unhandled rejection would silently abandon the click.
  const openGuidelinesDeck = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage(undefined);
    try {
      const { url } = await mpl.getDiagramGuidelinesDeck();
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      setErrorMessage(FETCH_FAILURE_MESSAGE);
    } finally {
      setIsLoading(false);
    }
  }, [mpl]);

  return { isLoading, errorMessage, openGuidelinesDeck, dismissError };
}
